import React, { forwardRef } from "react";

import {
  Button,
  ColProps,
  Container,
  Grid,
  Group,
  NumberInput,
  NumberInputProps,
  Select,
  SelectItem,
  Stack,
  Text,
  Title,
  TitleProps,
} from "@mantine/core";
import parse from "parse-unit";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useDocumentStyles } from "../../hooks/useDocumentStyles";
import { useCurrentEditorDocument } from "../../hooks/useEditorState";
import { SupportedFont, fontSelections, isSupportedFont } from "../../lib/model/fonts";
import { DocumentLayout, documentLayoutNames, documentLayouts } from "../../lib/model/layout";
import { defaultDocumentStyles } from "../../lib/state/defaultStyles";
import { librarySlice } from "../../lib/state/reducer";

import { CompactColorPicker } from "./CompactColorPicker";
import { FontWeightSelector } from "./FontWeightSelector";

export const StyleSettings = (): JSX.Element => {
  const styles = useDocumentStyles();
  const layout = useCurrentEditorDocument((state) => state.layout, "twoColumn");
  const reduxDispatch = useAppDispatch();

  const setFontFamily = React.useCallback(
    (newValue: string) => {
      if (isSupportedFont(newValue)) {
        reduxDispatch(librarySlice.actions.setDocumentFontFamily(newValue));
      }
    },
    [reduxDispatch]
  );

  const setLayout = React.useCallback(
    (newValue: DocumentLayout) => {
      reduxDispatch(librarySlice.actions.setDocumentLayout(newValue));
    },
    [reduxDispatch]
  );

  return (
    <Container size="sm" pb="xl">
      <Stack align="flex-start" spacing="xl">
        <Grid columns={2} align="center">
          <SettingHeader title="Document" />
          <Setting label="Layout">
            <Select
              value={layout}
              onChange={setLayout}
              data={documentLayouts.map((layout) => ({
                value: layout,
                label: documentLayoutNames[layout],
              }))}
              style={{ width: "20ch" }}
            />
          </Setting>
          <Setting label="Font">
            <FontFamilySelector
              value={styles.checklistDocument.fontFamily}
              onChange={setFontFamily}
            />
          </Setting>
          <Setting label="Page Margin">
            <NumericCssValueEditor
              value={styles.checklistDocument.padding}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setDocumentPadding(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>

          <SettingHeader title="Document Title" />
          <Setting label="Background Color">
            <CompactColorPicker
              value={styles.checklistDocumentTitle.backgroundColor}
              defaultSwatchColorIndex={9}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistDocumentTitle: { backgroundColor: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Text Color">
            <CompactColorPicker
              value={styles.checklistDocumentTitle.color}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistDocumentTitle: { color: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Font Size">
            <NumericCssValueEditor
              value={styles.checklistDocumentTitle.fontSize}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setDocumentTitleFontSize(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>
          <Setting label="Font Weight">
            <FontWeightSelector
              selected={styles.checklistDocumentTitle.fontWeight}
              defaultWeight={defaultDocumentStyles.checklistDocumentTitle.fontWeight}
              onClear={() => {
                reduxDispatch(librarySlice.actions.setDocumentTitleFontWeight(undefined));
              }}
              onSelect={(newValue) => {
                reduxDispatch(librarySlice.actions.setDocumentTitleFontWeight(newValue));
              }}
            />
          </Setting>

          <SettingHeader title="Checklist Titles" />
          <Setting label="Background Color">
            <CompactColorPicker
              value={styles.checklistTitle.backgroundColor}
              defaultSwatchColorIndex={9}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistTitle: { backgroundColor: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Text Color">
            <CompactColorPicker
              value={styles.checklistTitle.color}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistTitle: { color: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Font Size">
            <NumericCssValueEditor
              value={styles.checklistTitle.fontSize}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistTitleFontSize(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>
          <Setting label="Font Weight">
            <FontWeightSelector
              selected={styles.checklistTitle.fontWeight}
              defaultWeight={defaultDocumentStyles.checklistTitle.fontWeight || 400}
              onClear={() => {
                reduxDispatch(librarySlice.actions.setChecklistTitleFontWeight(undefined));
              }}
              onSelect={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistTitleFontWeight(newValue));
              }}
            />
          </Setting>

          <SettingHeader title="Checklist Headers" />
          <Setting label="Background Color">
            <CompactColorPicker
              value={styles.checklistHeader.backgroundColor}
              defaultSwatchColorIndex={1}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistHeader: { backgroundColor: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Text Color">
            <CompactColorPicker
              value={styles.checklistHeader.color}
              defaultSwatchColorIndex={9}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistHeader: { color: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Font Size">
            <NumericCssValueEditor
              value={styles.checklistHeader.fontSize}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistHeaderFontSize(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>
          <Setting label="Font Weight">
            <FontWeightSelector
              selected={styles.checklistHeader.fontWeight}
              defaultWeight={defaultDocumentStyles.checklistHeader.fontWeight || 400}
              onClear={() => {
                reduxDispatch(librarySlice.actions.setChecklistHeaderFontWeight(undefined));
              }}
              onSelect={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistHeaderFontWeight(newValue));
              }}
            />
          </Setting>
          <Setting label="Top Margin">
            <NumericCssValueEditor
              value={styles.checklistHeader.marginTop}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistHeaderTopMargin(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>

          <SettingHeader title="Checklist Items" />
          <Setting label="Background Color">
            <CompactColorPicker
              value={styles.checklistItem.backgroundColor}
              defaultSwatchColorIndex={1}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistItem: { backgroundColor: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Text Color">
            <CompactColorPicker
              value={styles.checklistItem.color}
              defaultSwatchColorIndex={9}
              onChange={(newColor) =>
                reduxDispatch(
                  librarySlice.actions.setDocumentStyles({
                    checklistItem: { color: newColor },
                  })
                )
              }
            />
          </Setting>
          <Setting label="Font Size">
            <NumericCssValueEditor
              value={styles.checklistItem.fontSize}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistItemFontSize(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>
          <Setting label="Font Weight">
            <FontWeightSelector
              selected={styles.checklistItem.fontWeight}
              defaultWeight={defaultDocumentStyles.checklistItem.fontWeight || 400}
              onClear={() => {
                reduxDispatch(librarySlice.actions.setChecklistItemFontWeight(undefined));
              }}
              onSelect={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistItemFontWeight(newValue));
              }}
            />
          </Setting>
          <Setting label="Spacing">
            <NumericCssValueEditor
              value={styles.checklistItem.marginTop}
              onChange={(newValue) => {
                reduxDispatch(librarySlice.actions.setChecklistItemVerticalMargin(newValue));
              }}
              inputProps={{
                style: { width: "6ch" },
              }}
            />
          </Setting>
        </Grid>
        <Button
          mt={"xl"}
          variant="outline"
          onClick={() => reduxDispatch(librarySlice.actions.setDocumentStyles(null))}
        >
          Reset to Defaults
        </Button>
      </Stack>
    </Container>
  );
};

const SettingHeader = ({ title, ...props }: { title: string } & TitleProps): JSX.Element => (
  <SmallCol span={2}>
    <Title order={3} {...props} pt={"xl"}>
      {title}
    </Title>
  </SmallCol>
);

const Setting = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element => {
  return (
    <>
      <SmallCol span={1}>{label}</SmallCol>
      <SmallCol span={1}>{children}</SmallCol>
    </>
  );
};

const SmallCol = ({
  children,
  ...props
}: { children: React.ReactNode } & ColProps): JSX.Element => {
  return (
    <Grid.Col {...props} py={4}>
      {children}
    </Grid.Col>
  );
};

const NumericCssValueEditor = ({
  value,
  onChange,
  inputProps,
}: {
  value: string;
  onChange: (newValue: string) => void;
  inputProps?: NumberInputProps;
}): JSX.Element => {
  const [currentScalar, currentUnit] = parse(value);

  const changeHandler = React.useCallback(
    (val: number | undefined) => {
      const returnedVal = val ? val : 0;
      onChange(`${returnedVal}${currentUnit}`);
    },
    [onChange, currentUnit]
  );
  return (
    <NumberInput
      size="xs"
      value={currentScalar}
      onChange={changeHandler}
      min={0}
      sx={(theme) => ({ input: { fontSize: theme.fontSizes.sm } })}
      {...inputProps}
    />
  );
};

const FontFamilySelector = ({
  value,
  onChange,
}: {
  value: SupportedFont;
  onChange: (newValue: SupportedFont) => void;
}): JSX.Element => {
  return (
    <Select
      value={value}
      onChange={onChange}
      data={fontSelections}
      itemComponent={FontFamilySelectItem}
      withinPortal={true}
      style={{ width: "20ch" }}
    />
  );
};

const FontFamilySelectItem = forwardRef<HTMLDivElement, SelectItem>(
  ({ label, value, ...others }: SelectItem, ref) => {
    return (
      <div ref={ref} {...others}>
        <Group noWrap>
          <Text style={{ fontFamily: value }}>{label}</Text>
        </Group>
      </div>
    );
  }
);
